import { WaitingIcon } from '@euler/app/components/icons/WaitingIcon';
import { Typography } from '@euler/app/components/typography';
import { useCurrentUserDangerously } from '@euler/app/flows/auth/context';
import { AnimationSource } from '@euler/assets';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { Icon } from '@euler/components/Icomoon';
import { config } from '@euler/config';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  useScreenContentContainerStyle,
  wrapNavigatorScreen,
} from '@euler/functions/wrapNavigatorScreen';
import { getSessionId } from '@euler/lib/session';
import { OrgMemberTransferRequest } from '@euler/model/entity';
import { TransferBatchStatus } from '@euler/model/enum';
import { useServiceFactory } from '@euler/services/factory';
import { makeDebug } from '@euler/utils';
import { formatMoney } from '@euler/utils/formatMoney';
import { usePersistFn } from '@euler/utils/hooks';
import LottieView from 'lottie-react-native';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { ScrollView } from 'react-native';

const debug = makeDebug('withdraw:result', __DEV__);

type Message = {
  type: 'transfer_batch_updated';
  data: {
    batchNo: string;
    transferBatchRequest: OrgMemberTransferRequest;
  };
};

export const WithdrawResultScreen = wrapNavigatorScreen(
  ({ batchNo }: { batchNo: string }) => {
    const contentContainerStyle = useScreenContentContainerStyle();
    const {
      user: { memberId },
    } = useCurrentUserDangerously();
    const { storeService, defaultTokenService } = useServiceFactory();
    const socketRef = useRef<WebSocket>();
    const timer = useRef<any>();
    const finished = useRef(false);

    const [transferBatch, setTransferBatch] =
      useState<OrgMemberTransferRequest>();

    const poll = usePersistFn(() => {
      const tick = async () => {
        try {
          const res =
            await storeService.getMemberAccountWithdrawTransferBatchRequest(
              memberId,
              batchNo,
            );
          if (
            res.batchStatus === TransferBatchStatus.Finished ||
            res.batchStatus === TransferBatchStatus.Closed
          ) {
            setTransferBatch(res);
            finished.current = true;
            timer.current = undefined;
          } else {
            timer.current = setTimeout(tick, 3000);
          }
        } catch (e) {
          console.error('error fetch transfer batch request: ', e);
          timer.current = setTimeout(tick, 3000);
        }
      };
      timer.current = setTimeout(tick, 3000);
    });

    const connect = useCallback(
      (clientId: string, token: string) => {
        const url = new URL(config.wsEndPoint);
        url.searchParams.append('clientid', clientId);
        url.searchParams.append('token', token);
        const socket = new WebSocket(url.toString());

        socket.addEventListener('open', () => {
          debug('opened websocket connection to %s', url);
          socketRef.current = socket;
          if (timer.current) {
            clearTimeout(timer.current);
            timer.current = undefined;
          }
        });

        socket.addEventListener('message', e => {
          const msg = JSON.parse(e.data) as Message;
          if (msg.type !== 'transfer_batch_updated') return;
          setTransferBatch(msg.data.transferBatchRequest);
          socket.close();
          socketRef.current = undefined;
          finished.current = true;
          if (timer.current) {
            clearTimeout(timer.current);
            timer.current = undefined;
          }
        });

        socket.addEventListener('error', e => {
          debug(
            'an error occurred while connecting to %s: ',
            url,
            (e as any).message,
          );
          if (!finished.current) {
            poll();
          }
        });

        socket.addEventListener('close', () => {
          debug('websocket connection closed');
          if (!finished.current) {
            poll();
          }
        });
      },
      [poll],
    );

    useEffect(() => {
      if (finished.current) return;

      Promise.all([getSessionId(), defaultTokenService.getToken()])
        .then(([{ sessionId }, token]) => {
          connect(sessionId, token!);
        })
        .catch(err => {
          alert((err as any).message);
        });

      return () => {
        if (socketRef.current) {
          socketRef.current.close();
          socketRef.current = undefined;
        }
        if (timer.current) {
          clearTimeout(timer.current);
          timer.current = undefined;
        }
      };
    }, [connect, defaultTokenService, poll]);

    return (
      <ScrollView
        contentContainerStyle={[{ flexGrow: 1 }, contentContainerStyle]}
      >
        {transferBatch == null ? (
          <PendingContent />
        ) : (
          <ResultContent request={transferBatch} />
        )}
      </ScrollView>
    );
  },
  {
    title: '提现结果',
  },
);

const PendingContent = memo(() => {
  const { rpx } = useResponsiveContext();
  return (
    <Center
      css={`
        flex: 1;
        padding-bottom: ${rpx(120)}px;
        padding-left: ${rpx(30)}px;
        padding-right: ${rpx(30)}px;
      `}
    >
      <WaitingIcon size={rpx(100)} fill={'#777'} />
      <Typography.Title
        size={rpx(24)}
        medium
        css={`
          margin-top: ${rpx(10)}px;
          margin-bottom: ${rpx(10)}px;
        `}
      >
        提现处理中
      </Typography.Title>
      <Typography.Text>
        已提交提现请求，这可能需要几分钟时间，请稍候...
      </Typography.Text>
    </Center>
  );
});

const ResultContent = memo(
  ({ request }: { request: OrgMemberTransferRequest }) => {
    const { rpx } = useResponsiveContext();
    const success =
      request.batchStatus === TransferBatchStatus.Finished &&
      request.successAmount != null &&
      request.successAmount > 0;
    return (
      <Center
        css={`
          flex: 1;
          padding-bottom: ${rpx(120)}px;
          padding-left: ${rpx(30)}px;
          padding-right: ${rpx(30)}px;
        `}
      >
        <Icon
          name={success ? 'checkmark-circle' : 'closecircle'}
          color={success ? StatusColors.Success : StatusColors.Danger}
          size={rpx(100)}
        />
        <Typography.Title
          size={rpx(28)}
          css={`
            margin-top: ${rpx(10)}px;
            margin-bottom: ${rpx(10)}px;
          `}
        >
          {request.batchStatus === TransferBatchStatus.Finished
            ? '提现已完成'
            : '提现失败'}
        </Typography.Title>
        {request.batchStatus === TransferBatchStatus.Finished ? (
          request.successAmount != null && request.successAmount > 0 ? (
            <Typography.Text align="center" size={rpx(16)}>
              已提现
              <Typography.Text color="red">
                {formatMoney(request.successAmount)}
              </Typography.Text>
              元到您的微信零钱钱包，请查收！
              {'\n若对本次交易有任何疑问，请联系我们！'}
            </Typography.Text>
          ) : (
            <Typography.Text>
              本次转账未能完成，交易已关闭 ({request.closeReason ?? '未知原因'}
              )。若对本次交易有任何疑问，请联系我们！
            </Typography.Text>
          )
        ) : (
          <Typography.Text>
            本次转账未能完成，交易已关闭 ({request.closeReason ?? '未知原因'}
            )。若对本次交易有任何疑问，请联系我们！
          </Typography.Text>
        )}
        {success && (
          <LottieView
            source={AnimationSource.confetti}
            autoPlay
            loop={false}
            style={{
              position: 'absolute',
              left: -rpx(80),
              right: -rpx(80),
              top: -rpx(100),
              bottom: rpx(100),
            }}
          />
        )}
      </Center>
    );
  },
);
